import React, { useEffect, useRef, useState } from 'react'
import styles from './CanvasLab.module.scss'

// These are existing Lugsail brand-theme fields. Canvas only supplies compact
// presets; the shared SVG transform pipeline continues to perform the layout.
const LABEL_ACTIONS = [
  {
    id: 'auto',
    label: 'Fit labels automatically',
    patch: { labelHandlingMode: 'auto', labelShowEveryNth: 1, labelShowFullOnHover: true },
  },
  {
    id: 'angled',
    label: 'Angle x labels',
    patch: { labelHandlingMode: 'manual', labelOverflowStrategy: 'angled', labelShowEveryNth: 1 },
  },
  {
    id: 'shorten',
    label: 'Shorten x labels',
    patch: { labelHandlingMode: 'manual', labelOverflowStrategy: 'truncate', labelShowEveryNth: 1 },
  },
  {
    id: 'stagger',
    label: 'Stagger x labels',
    patch: { labelHandlingMode: 'manual', labelOverflowStrategy: 'stagger', labelShowEveryNth: 1 },
  },
  {
    id: 'every-second',
    label: 'Show every second label',
    patch: { labelHandlingMode: 'auto', labelShowEveryNth: 2, labelShowFullOnHover: true },
  },
]

/** Compact, opt-in layout treatments for the selected chart. */
export default function CanvasLayoutActions({
  brandTheme,
  onBrandThemeChange,
  marginSuggestions = null,
  onVisualOptionsChange = null,
  hasLegendData = false,
}) {
  const [pendingId, setPendingId] = useState(null)
  const frameRef = useRef(null)

  useEffect(() => () => {
    if (frameRef.current) window.cancelAnimationFrame(frameRef.current)
  }, [])

  useEffect(() => {
    const pendingAction = LABEL_ACTIONS.find((action) => action.id === pendingId)
    if (!pendingAction) return
    const applied = Object.entries(pendingAction.patch).every(
      ([key, value]) => brandTheme?.[key] === value
    )
    if (applied) setPendingId(null)
  }, [brandTheme, pendingId])

  const applyAction = (action) => {
    if (frameRef.current) window.cancelAnimationFrame(frameRef.current)
    setPendingId(action.id)

    // Paint the selected state first. Rendering a real RAWGraphs SVG can be
    // expensive, and otherwise the browser often appears to ignore the click.
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null
      onBrandThemeChange({ ...brandTheme, ...action.patch })
    })
  }

  const applyMarginRecommendations = () => {
    if (!marginSuggestions || !onVisualOptionsChange) return
    onVisualOptionsChange((current) => ({ ...current, ...marginSuggestions }))
  }

  return <section className={styles.layoutActions} aria-label="Layout actions">
    <h3>Try a layout treatment</h3>
    <p>These update this chart only. Duplicate first to compare alternatives.</p>
    <div className={styles.layoutActionButtons}>
      {LABEL_ACTIONS.map((action) => {
        const active = Object.entries(action.patch).every(
          ([key, value]) => brandTheme?.[key] === value
        )
        const pending = pendingId === action.id
        return <button
          key={action.id}
          type="button"
          aria-pressed={active}
          aria-busy={pending}
          className={pending ? styles.layoutActionPending : ''}
          onClick={() => applyAction(action)}
        >
          {pending ? 'Applying layout…' : action.label}
        </button>
      })}
      {marginSuggestions && onVisualOptionsChange && <button
        type="button"
        onClick={applyMarginRecommendations}
      >
        Apply recommended chart margins
      </button>}
    </div>
    {hasLegendData && <div>
      <h3>Legend placement</h3>
      <label>
        Position
        <select
          value={brandTheme?.legendPlacement || 'auto'}
          onChange={(event) => onBrandThemeChange({ ...brandTheme, legendPlacement: event.target.value })}
        >
          <option value="auto">Chart default</option>
          <option value="top">Top</option>
          <option value="right">Right</option>
          <option value="bottom">Bottom</option>
          <option value="left">Left</option>
        </select>
      </label>
      <label>
        Legend content scale <span>{Math.round((Number(brandTheme?.legendScale) || 1) * 100)}%</span>
        <input
          type="range"
          min="0.7"
          max="1.4"
          step="0.05"
          value={Number(brandTheme?.legendScale) || 1}
          onChange={(event) => onBrandThemeChange({ ...brandTheme, legendScale: Number(event.target.value) })}
        />
      </label>
    </div>}
  </section>
}
